import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchProductById, fetchProductReviews } from '../api/woocommerce';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';

function round(value, precision) {
  var multiplier = Math.pow(10, precision || 0);
  return Math.round(value * multiplier) / multiplier;
}

// Star Rating component for display (using the same style as ProductPage)
const StarRating = ({ rating }) => {
  const FilledStar = () => (
    <svg
      width="8"
      height="8"
      viewBox="0 0 8 8"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M4 0L5.235 2.635L8 3.055L6 5.105L6.47 8L4 6.635L1.53 8L2 5.105L0 3.055L2.765 2.635L4 0Z"
        fill="black"
      />
    </svg>
  );

  const OutlinedStar = () => (
    <svg
      width="8"
      height="8"
      viewBox="0 0 8 8"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_4001_208)">
        <path
          d="M4 1.175L4.785 2.845L4.9 3.085L5.165 3.125L6.965 3.4L5.645 4.755L5.47 4.935L5.51 5.185L5.815 7.065L4.245 6.195L4.005 6.06L3.765 6.195L2.195 7.065L2.5 5.185L2.54 4.935L2.365 4.755L1.045 3.4L2.845 3.125L3.11 3.085L3.225 2.845L4.01 1.175M4 0L2.765 2.635L0 3.055L2 5.105L1.53 8L4 6.635L6.47 8L6 5.105L8 3.055L5.235 2.635L4 0Z"
          fill="black"
        />
      </g>
      <defs>
        <clipPath id="clip0_4001_208">
          <rect width="8" height="8" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );

  return (
  <div className="flex items-center translate-y-[1px]">
      {[1, 2, 3, 4, 5].map((index) => (
        <span key={index} className='mr-[4px]'>
          {index <= rating ? <FilledStar /> : <OutlinedStar />}
        </span>
      ))}
    </div>
  );
};

// Large Star Rating component (matching ProductPage)
const StarRatingLarge = ({ rating }) => {
  const FilledStarL = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="black" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 0L15.705 7.905L24 9.165L18 15.315L19.41 24L12 19.905L4.59 24L6 15.315L0 9.165L8.295 7.905L12 0Z" />
    </svg>
  );

  const EmptyStarL = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="black" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 3.525L14.355 8.535L14.7 9.255L15.495 9.375L20.895 10.2L16.935 14.265L16.41 14.805L16.53 15.555L17.445 21.195L12.735 18.585L12.015 18.18L11.295 18.585L6.585 21.195L7.5 15.555L7.62 14.805L7.095 14.265L3.135 10.2L8.535 9.375L9.33 9.255L9.675 8.535L12.03 3.525M12 0L8.295 7.905L0 9.165L6 15.315L4.59 24L12 19.905L19.41 24L18 15.315L24 9.165L15.705 7.905L12 0Z" />
    </svg>
  );

  return (
    <div className="flex items-center">
      {[1, 2, 3, 4, 5].map((index) => (
        <span key={index} className="mr-[8px]">
          {index <= rating ? <FilledStarL /> : <EmptyStarL />}
        </span>
      ))}
    </div>
  );
};

// Loading Skeleton Component
const LoadingSkeleton = () => (
  <div className="px-2">
    <hr className="border-t border-black" />

    {/* Header Skeleton */}
    <div className="py-2">
      <div className="h-8 bg-[#BDBDBD] animate-pulse mb-2 w-1/4"></div>
      <div className="h-8 bg-[#BDBDBD] animate-pulse w-1/3"></div>
    </div>

    <hr className="border-t border-black" />

    {/* Reviews Summary Skeleton */}
    <div className="flex flex-col lg:flex-row gap-2 w-full">
      <div className="w-full lg:w-1/2 flex flex-col justify-center pb-2 pt-4 md:pb-4">
        <div className="h-12 bg-[#BDBDBD] animate-pulse mb-4 w-16"></div>
        <div className="flex items-end mb-0">
          <div className="flex items-center">
            {[1, 2, 3, 4, 5].map((index) => (
              <div key={index} className="w-6 h-6 bg-[#BDBDBD] animate-pulse mr-2"></div>
            ))}
          </div>
          <div className="ml-4 h-5 bg-[#BDBDBD] animate-pulse w-24"></div>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex flex-col justify-center md:border-l lg:border-t-0 border-t border-black pb-[9px] pt-[15px] lg:pl-2">
        <div className="h-12 bg-[#BDBDBD] animate-pulse mb-2 w-20"></div>
        <div className="h-12 bg-[#BDBDBD] animate-pulse w-32"></div>
      </div>
    </div>

    <hr className="border-t border-black" />

    {/* Individual Reviews Skeleton */}
    <div className="space-y-8 mb-2">
      {[1, 2, 3].map((index) => (
        <div key={index} className="border-b border-black pb-0">
          <div className="h-4 bg-[#BDBDBD] animate-pulse mt-4 w-1/3"></div>
          <div className="space-y-2 my-4">
            <div className="h-4 bg-[#BDBDBD] animate-pulse w-full"></div>
            <div className="h-4 bg-[#BDBDBD] animate-pulse w-5/6"></div>
            <div className="h-4 bg-[#BDBDBD] animate-pulse w-3/4"></div>
          </div>
          <div className="mb-4 lg:flex lg:items-center">
            <div className="flex items-center mb-2 lg:mb-0">
              <div className="h-4 bg-[#BDBDBD] animate-pulse w-20 mr-2"></div>
              <div className="h-4 bg-[#BDBDBD] animate-pulse w-16"></div>
            </div>
            <div className="flex items-center">
              <div className="flex items-center">
                {[1, 2, 3, 4, 5].map((starIndex) => (
                  <div key={starIndex} className="w-2 h-2 bg-[#BDBDBD] animate-pulse mr-1"></div>
                ))}
              </div>
              <div className="ml-2 h-3 bg-[#BDBDBD] animate-pulse w-12"></div>
              <div className="ml-2 h-3 bg-[#BDBDBD] animate-pulse w-20"></div>
            </div>
          </div>
        </div>
      ))}
    </div>

    {/* Pagination Skeleton */}
    <div className="flex justify-center items-center gap-4 pb-[64px]">
      <div className="w-4 h-4 bg-[#BDBDBD] animate-pulse"></div>
      <div className="h-4 bg-[#BDBDBD] animate-pulse w-12"></div>
      <div className="w-4 h-4 bg-[#BDBDBD] animate-pulse"></div>
    </div>
  </div>
);

const AllReviewsPage = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const reviewsPerPage = 10; // Number of reviews per page
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const loadProductAndReviews = async () => {
      try {
        setLoading(true);
        setError(null);

        const productData = await fetchProductById(id, currentLanguage);
        setProduct(productData);

        // Load reviews
        try {
          const reviewsData = await fetchProductReviews(id);
          setReviews(reviewsData);
        } catch (reviewErr) {
          console.error('Error loading reviews:', reviewErr);
        }
      } catch (err) {
        console.error('Error loading product:', err);
        setError('Failed to load product details');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadProductAndReviews();
    }
  }, [id, currentLanguage]);

  // Show loading state
  if (loading) {
    return <LoadingSkeleton />;
  }

  // Show error state
  if (error || !product) {
    return (
      <div className="px-2 pt-16">
        <hr className="border-t border-black" />
        <div className="py-8 text-center">
          <div className="mb-16">

          </div>
        </div>
      </div>
    );
  }

  // Calculate average rating from reviews
  const averageRating = reviews.length > 0
    ? (reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length)
    : parseFloat(product.average_rating) || 0;

  // Calculate pagination
  const totalPages = Math.ceil(reviews.length / reviewsPerPage);
  const startIndex = (currentPage - 1) * reviewsPerPage;
  const endIndex = startIndex + reviewsPerPage;
  const currentReviews = reviews.slice(startIndex, endIndex);

  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
    // Scroll to top when changing pages
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
    // Scroll to top when changing pages
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="px-2">
      <hr className="border-t border-black" />

      {/* Header */}
      <div className="py-2">
        <h1 className="text-26 font-light mb-2">{t('reviews.title')}</h1>
        <h2 className="text-26 font-normal">{product?.name?.toLowerCase()}</h2>
      </div>

      <hr className="border-t border-black" />

      {/* Reviews Summary */}
      <div className="flex flex-col lg:flex-row gap-2 w-full">
        <div className="w-full lg:w-1/2 flex flex-col justify-center pb-2 pt-4 md:pb-4">
          <div className="text-48 font-light mb-[16px] leading-48">
            {averageRating.toFixed(1)}
          </div>
          <div className="flex items-end mb-0">
            <StarRatingLarge rating={Math.round(averageRating)} />
            <span className="ml-[16px] text-18 font-light transform translate-y-[3px]">
              ({reviews.length || product.rating_count || 0} {t('reviews.reviewsCount')})
            </span>
          </div>
        </div>

        <div className="w-full lg:w-1/2 flex flex-col justify-center md:border-l lg:border-t-0 border-t border-black pb-[9px] pt-[15px] lg:pl-2">
          <div className="text-48 font-light leading-48">
            {reviews.length > 0
    ? Math.round((reviews.filter(r => r.recommend === 'yes').length / reviews.length) * 100)
    : 0}% {t('reviews.people')}
          </div>
          <div className="text-[48px] font-light leading-48">{t('reviews.wouldRecommend')}</div>
        </div>
      </div>

      <hr className="border-t border-black" />

      {/* Individual Reviews */}
      <div className="mb-2">
        {currentReviews && currentReviews.length > 0 ? (
          currentReviews.map((review) => {
            // Remove HTML tags (like <p>) from review content
            const cleanReview = review.review.replace(/<[^>]+>/g, '');

            // Extract headline from the review HTML
            const headlineMatch = review.review.match(/<strong>(.*?)<\/strong>/);
            const headline = headlineMatch ? headlineMatch[1] : '';

            // Remove the headline from the main review content
            const reviewWithoutHeadline = review.review
              .replace(/<p><strong>.*?<\/strong><\/p>\s*/g, '')
              .replace(/<[^>]+>/g, '');

                const wouldRecommend = review.recommend === 'yes';

            return (
              <div key={review.id} className="border-b border-black pb-0">
                {headline && (
                  <p className="font-normal text-14 mt-4">{headline}</p>
                )}
                <p className="font-light text-14 my-4 leading-relaxed">
                  {reviewWithoutHeadline || cleanReview}
                </p>
                <div className="text-14 font-normal mb-[16px] lg:flex lg:items-center">
                  <div className="flex items-center">
                    <span className="mr-2">{review.reviewer}</span>
                    <span className="mr-2">/</span>
                    <span className="mr-4">
                      {new Date(review.date_created).toLocaleDateString('en-GB')}
                    </span>
                  </div>
                  <div className="flex items-baseline  md:mt-0 mt-2 ">
                    <StarRating rating={review.rating} />
                    <span className="ml-[2px] text-10 font-normal ">
                      ({round(review.rating, 1)} {t('reviews.stars')})
                    </span>
                    <span className="ml-[6px] text-10 font-medium">
                      {wouldRecommend ? t('reviews.WouldRecommend') : t('reviews.wouldNotRecommendReviews')}
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="py-8 text-center">
            <p className="text-[#BDBDBD] font-normal text-24">{t('reviews.noReviews')}</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      <div className="flex justify-center items-center gap-4 pb-[64px]">
        <button
          onClick={handlePreviousPage}
          disabled={currentPage === 1}
          className={`text-14 ${currentPage === 1 ? 'text-[#BDBDBD] cursor-not-allowed' : 'text-black hover:underline'}`}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0_4001_703)">
              <path d="M11.6362 14.9091L8.3635 11.4545L5.09078 7.99997L11.6362 1.09088" stroke="black" strokeMiterlimit="10" />
            </g>
            <defs>
              <clipPath id="clip0_4001_703">
                <rect width="16" height="16" fill="white" transform="translate(16 16) rotate(-180)" />
              </clipPath>
            </defs>
          </svg>
        </button>

        <span className="text-14 font-light">
          {t('reviews.page')} {currentPage}
        </span>

        <button
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
          className={`text-14  font-light ${currentPage === totalPages ? 'text-black cursor-not-allowed' : 'text-black hover:underline'}`}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0_4001_700)">
              <path d="M4.36377 1.09094L10.9092 8.00003L4.36377 14.9091" stroke="black" strokeMiterlimit="10" />
            </g>
            <defs>
              <clipPath id="clip0_4001_700">
                <rect width="16" height="16" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </button>
      </div>
    </div>
  );
};

export default AllReviewsPage;